// Koordynacja połączeń przychodzących między wieloma zakładkami tej samej przeglądarki.
// - Tylko jedna zakładka odtwarza dzwonek (elekcja przez Web Locks API).
// - Wszystkie zakładki pokazują okno accept/decline.
// - Gdy user odbierze/odrzuci w jednej zakładce, inne zamykają okno (BroadcastChannel).

export type CallResolution = 'accepted' | 'declined' | 'timeout';

const CHANNEL_NAME = 'call-coordination-v1';
const RINGTONE_TTL_MS = 35000; // Nieco dłużej niż 30s auto-decline na dzwonek.

let channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL_NAME);
    }
  } catch {
    channel = null;
  }
  return channel;
}

export function buildCallKey(payload: { meetLink?: string | null; fromUserId?: string | null; toUserId?: string | null }): string {
  const meet = payload?.meetLink || '';
  const from = payload?.fromUserId || '';
  const to = payload?.toUserId || '';
  return `incoming-call:${to}:${from}:${meet}`;
}

// Próbuje zostać jedyną zakładką, która odtwarza dzwonek dla danego połączenia.
// Zwraca funkcję release() – wywołaj, gdy dzwonek nie jest już potrzebny (np. odbiór/odrzut).
// Jeżeli inna zakładka już trzyma blokadę, zwraca null.
export async function acquireRingtoneLeadership(callKey: string): Promise<(() => void) | null> {
  const locks: any = (typeof navigator !== 'undefined' ? (navigator as any).locks : null);
  if (locks && typeof locks.request === 'function') {
    return new Promise<(() => void) | null>((resolve) => {
      let decided = false;
      let releaseFn: (() => void) | null = null;
      locks.request(callKey, { ifAvailable: true }, (lock: any) => {
        if (!lock) {
          if (!decided) { decided = true; resolve(null); }
          return;
        }
        return new Promise<void>((release) => {
          releaseFn = () => { try { release(); } catch { /* ok */ } };
          if (!decided) { decided = true; resolve(releaseFn); }
          // Safety net – po TTL zwolnij blokadę, nawet jeśli nikt nie wywołał release.
          setTimeout(() => { try { release(); } catch { /* ok */ } }, RINGTONE_TTL_MS);
        });
      });
    });
  }

  // Fallback bez Web Locks – prosta blokada przez localStorage z TTL.
  try {
    const k = `call:ringtone:${callKey}`;
    const now = Date.now();
    const raw = localStorage.getItem(k);
    const prev = raw ? parseInt(raw, 10) : 0;
    if (prev && now - prev < RINGTONE_TTL_MS) return null;
    localStorage.setItem(k, String(now));
    let released = false;
    return () => {
      if (released) return;
      released = true;
      try {
        const current = localStorage.getItem(k);
        if (current === String(now)) localStorage.removeItem(k);
      } catch { /* ok */ }
    };
  } catch {
    // Jeśli storage nie działa – pozwól zadzwonić (lepiej duplikat niż cisza).
    return () => {};
  }
}

// Powiadamia pozostałe zakładki, że połączenie zostało rozstrzygnięte.
export function broadcastCallResolved(callKey: string, resolution: CallResolution) {
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: 'call:resolved', callKey, resolution, ts: Date.now() });
  } catch { /* ok */ }
}

// Subskrypcja rozstrzygnięć z innych zakładek. Zwraca unsubscribe.
export function onCallResolvedFromOtherTab(
  handler: (data: { callKey: string; resolution: CallResolution }) => void
): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (ev: MessageEvent) => {
    const msg = ev?.data;
    if (!msg || msg.type !== 'call:resolved') return;
    handler({ callKey: String(msg.callKey || ''), resolution: msg.resolution });
  };
  ch.addEventListener('message', listener as any);
  return () => {
    try { ch.removeEventListener('message', listener as any); } catch { /* ok */ }
  };
}
